import React, { createContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { useDispatch, useSelector } from 'react-redux';
import { updateToken } from '../redux/auth.slice';
import { fetchOrders, setOrdersCount } from '../pages/OrdersPage/slice/orders.slice';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

export const NotificationContext = createContext({
  orders: 0,
  applications: 0,
});

export const NotificationProvider = ({ children }) => {
  const [lastMessage, setLastMessage] = useState(null);
  const [isOrder, setIsOrder] = useState(false);

  const { page, tab, rowsPerPage, toOrderCount } = useSelector((state) => state.orders);

  const dispatch = useDispatch();

  useEffect(() => {
    const initNotifications = async () => {
      try {
        if (Notification.permission === 'granted') {
          const token = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
          });
          if (token) {
            dispatch(updateToken(token));
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Notification token error:', error);
        }
      }
    };

    initNotifications();

    const unsubscribe = onMessage(messaging, (payload) => {
      setIsOrder(true);
      setLastMessage(payload.data);
      toast.info(payload?.notification?.body);

      const audio = new Audio('/assets/songs/ringtone.mp3');
      audio.play().catch(() => {});
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isOrder) {
      dispatch(fetchOrders({ status: tab, page, page_size: rowsPerPage }));
      setIsOrder(false);
    }
  }, [tab, dispatch, page, rowsPerPage, isOrder]);

  useEffect(() => {
    if (toOrderCount) {
      dispatch(setOrdersCount(toOrderCount));
    }
  }, [dispatch, toOrderCount]);

  const requestPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        });
        if (token) {
          dispatch(updateToken(token));
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Notification permission error:', error);
      }
    }
  };

  const value = useMemo(() => ({ lastMessage, isOrder, requestPermission }), [lastMessage, isOrder]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
